import IconButton from '@/components/Buttons/IconButton';
import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import { toast } from '@/components/toast';
import { useGetPresentersList } from '@/hooks';
import RemoveBackgroundIcon from '@/images/RemoveBackground.svg';
import { useAddAvatarToPolotno } from '@/pages/AdvancedEditor/components/panels/avatars/utils/useAddAvatarToPolotno';
import { useAdvancedEditorContext } from '@/pages/AdvancedEditor/context/useAdvancedEditorContext';
import { PolotnoStoreKeys, usePolotnoState } from '@/pages/AdvancedEditor/hooks';
import { useGetCurrentPresenter } from '@/pages/AdvancedEditor/hooks/useGetCurrentPresenter';
import { getAvatarElementFromPage, getAvatarPresenterFromPage } from '@/pages/AdvancedEditor/utils';
import { useAnalytics } from '@/services/analytics';
import { uploadImage } from '@/services/api';
import { logger } from '@/services/logger';
import theme from '@/theme';
import { ImageType, Presenter, PresenterUsecase, VideoType } from '@d-id/types';
import styled from '@emotion/styled';
import imglyBackgroundRemoval from '@imgly/background-removal';
import Tooltip from '@mui/material/Tooltip';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useState } from 'react';

const RemoveBackgroundButton = styled(IconButton)`
    color: ${({ theme }) => theme.colors.secondary.main} !important;
    background-color: #fff !important;
    font-weight: 600;
    border-radius: 8px;
    margin-top: 5px;
    display: inline-flex;
    font-size: 16px;
    width: 34px;
    height: 34px;

    p {
        margin-left: 8px;
    }

    :focus {
        outline: none;
    }

    :hover {
        outline: 1px solid ${({ theme }) => theme.colors.black[200]};
        background-color: ${({ theme }) => theme.colors.black[200]} !important;
    }
`;

const RestoreBackgroundButton = styled(IconButton)`
    color: ${({ theme }) => theme.colors.white};
    border-radius: 8px;
    background-color: ${({ theme }) => theme.colors.secondary[900]} !important;
    display: inline-flex;
    font-size: 16px;

    :focus {
        outline: none;
    }

    :hover {
        outline: 1px solid ${({ theme }) => theme.colors.black[200]};
        background-color: ${({ theme }) => theme.colors.black[700]} !important;
    }
`;

const useRemoveBackground = () => {
    const { refetch: refetchPresentersList } = useGetPresentersList();

    const removeBackground = async (path: string, id: string, presenterType = ImageType.Upload): Promise<void> => {
        try {
            const response = await fetch(path);
            const blob = await response.blob();

            const imageBlob = await imglyBackgroundRemoval(blob);

            const fileName = path.split('/').pop();
            const name = fileName!.split('.')[0];

            const file = new File([imageBlob], `${name}-removed_bg.png`, {
                type: 'image/png',
            });

            await uploadImage({
                image: file,
                metadata: {
                    id,
                    presenter_type: presenterType,
                },
            });

            await refetchPresentersList();
        } catch (error) {
            logger.error('Error while removing background', {
                error,
                image: path,
            });
            toast.error({ title: 'Error while removing background. Please try again', toastId: `background-remove-error` });
        }
    };

    return { removeBackground };
};

export const RemoveBackground = observer(() => {
    const mixpanel = useAnalytics();
    const { store } = useAdvancedEditorContext();

    const currentPresenter = getAvatarPresenterFromPage(store.activePage);
    const [removeLoadingSet] = usePolotnoState(PolotnoStoreKeys.backgroundLoading);
    const [bgReady, setBgReady] = usePolotnoState(PolotnoStoreKeys.backgroundReady);

    const { removeBackground } = useRemoveBackground();
    const [removeBackgroundReported, setRemoveBackgroundReported] = useState(false);

    const addAvatar = useAddAvatarToPolotno();
    const { refetch: refetchPresentersList } = useGetPresentersList({
        usecase: PresenterUsecase.Video,
        presenterType: ImageType.UserPresenters,
    });
    const presenter = useGetCurrentPresenter(ImageType.UserPresenters);

    const parsedPresenter = JSON.parse(JSON.stringify(currentPresenter));

    const [isModified, setIsModified] = useState<boolean>();

    useEffect(() => {
        setIsModified(parsedPresenter.id.includes('-removed_bg'));
    }, [parsedPresenter.id]);

    useEffect(() => {
        if (presenter && `${presenter.id}-removed_bg` === bgReady) {
            updateAvatar(`${presenter.id}-removed_bg`);
            setBgReady(undefined);
        }
    }, [bgReady]);

    const updateAvatar = async (id: string) => {
        const { data: newPresenters } = await refetchPresentersList();
        const presenter = newPresenters?.find(p => p.id === id);
        const avatarElement = getAvatarElementFromPage(store.activePage);

        if (presenter && avatarElement) {
            await addAvatar(
                store.activePage,
                {
                    ...presenter,
                    x: avatarElement.x,
                    y: avatarElement.y,
                    width: avatarElement.width,
                    height: avatarElement.height,
                    originalWidth: avatarElement.custom.originalWidth,
                    originalHeight: avatarElement.custom.originalHeight,
                } as Presenter,
                { skipCrop: true }
            );
        }
    };

    const handleRemoveBackground = async () => {
        if (!removeBackgroundReported && mixpanel) {
            mixpanel.track('background-removed', { event: 'click' });
            setRemoveBackgroundReported(true);
        }

        if (parsedPresenter?.poster) {
            setIsModified(true);
            const id = `${parsedPresenter.id}-removed_bg`;
            removeLoadingSet.add(id);

            const { data: newPresenters } = await refetchPresentersList();
            const alreadyExists = newPresenters?.find(p => p.id === id);

            if (!alreadyExists) {
                await removeBackground(parsedPresenter.poster, id, parsedPresenter?.custom?.presenter?.presenterType);
            }

            removeLoadingSet.delete(id);
            setBgReady(id);
        }
    };

    const handleRestoreBackground = async () => {
        setIsModified(false);
        if (presenter?.id) {
            const id = presenter.id.replace('-removed_bg', '');
            await updateAvatar(id);
        }
    };

    const isLoading = useMemo(() => {
        const id = parsedPresenter.id;
        return removeLoadingSet.has(id) || removeLoadingSet.has(`${id}-removed_bg`);
    }, [parsedPresenter]);

    const showFeature =
        currentPresenter &&
        currentPresenter.type !== VideoType.CLIP &&
        currentPresenter.presenterType &&
        [ImageType.Upload, ImageType.Dreams].includes(currentPresenter.presenterType) &&
        !currentPresenter.isDefault;

    if (!showFeature) {
        return null;
    }

    if (isLoading) {
        return <RemoveBackgroundButton ariaLabel="loading" isLoading={isLoading} />;
    }

    if (isModified) {
        return (
            <Tooltip
                placement="top"
                title="Undo"
                slotProps={{ tooltip: { sx: { backgroundColor: theme.colors.secondary[900], padding: '8px', fontSize: '14px' } } }}
                PopperProps={{
                    modifiers: [{ name: 'offset', options: { offset: [0, -6] } }],
                }}>
                <div data-testid="undo-remove-background-button">
                    <RestoreBackgroundButton ariaLabel="restore-background" onClick={handleRestoreBackground}>
                        <MaskedIcon src={RemoveBackgroundIcon} size="18px" />
                    </RestoreBackgroundButton>
                </div>
            </Tooltip>
        );
    }

    return (
        <Tooltip
            placement="top"
            title="Remove background"
            slotProps={{ tooltip: { sx: { backgroundColor: theme.colors.secondary[900], padding: '8px', fontSize: '14px' } } }}
            PopperProps={{
                modifiers: [{ name: 'offset', options: { offset: [0, -6] } }],
            }}>
            <div data-testid="remove-background-button">
                <RemoveBackgroundButton ariaLabel="remove-background-button" onClick={handleRemoveBackground} isLoading={isLoading}>
                    <MaskedIcon src={RemoveBackgroundIcon} size="18px" />
                </RemoveBackgroundButton>
            </div>
        </Tooltip>
    );
});
